import { useEffect, useRef } from 'react';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Heading } from '~/components/molecules/Heading';
import { getRegionFromPath, useNavigate } from '~/components/utils/Navigation';
import { useApi } from '~/lib/api';
import { useAcceptInvitationMutation } from '~/lib/api/modules/Invitations/hooks/useAcceptInvitationMutation';

export type IAcceptInvitationPageProps = {
  organizationId: string;
  invitationId: string;
  challengeCode: string;
};

export const AcceptInvitationPage: React.FC<IAcceptInvitationPageProps> = (
  props,
) => {
  const { organizationId, invitationId, challengeCode } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const location = useLocation();
  const currentAppRegion = api.getAuthorization()?.region;
  const appRegion = getRegionFromPath(location.pathname.split('/')[1]);
  const isSameRegion = currentAppRegion === appRegion;
  const acceptInvitationMutation = useAcceptInvitationMutation();

  const isInitRef = useRef(false);
  useEffect(() => {
    if (isInitRef.current) {
      return;
    }
    isInitRef.current = true;

    if (
      acceptInvitationMutation.isLoading ||
      acceptInvitationMutation.isError
    ) {
      return;
    }

    void acceptInvitationMutation
      .mutateAsync({
        organizationId,
        invitationId,
        body: { challengeCode },
      })
      .then(() => navigate(`/organizations/${organizationId}`));
  }, [
    acceptInvitationMutation,
    organizationId,
    invitationId,
    challengeCode,
    navigate,
  ]);

  return (
    <>
      <Helmet>
        <title>{t('AcceptInvitationPage.seo.title')}</title>
      </Helmet>

      <Heading
        icon={<FontAwesomeIcon icon={faEnvelope} />}
        title={t('AcceptInvitationPage.title')}
      />

      <ContentBlock>
        <ContentBlock.Body first>
          {isSameRegion ? (
            acceptInvitationMutation.isError ? (
              <ErrorMessage error={acceptInvitationMutation.error} />
            ) : acceptInvitationMutation.isLoading ? (
              t('common.loading')
            ) : null
          ) : (
            <>
              <Trans
                parent="p"
                i18nKey="AcceptInvitationPage.sameRegion.p1"
                values={{ requestedAppRegion: appRegion, currentAppRegion }}
              />
              <Trans
                parent="p"
                i18nKey="AcceptInvitationPage.sameRegion.p2"
                values={{ requestedAppRegion: appRegion, currentAppRegion }}
              />
              <Trans
                parent="p"
                i18nKey="AcceptInvitationPage.sameRegion.p3"
                values={{ requestedAppRegion: appRegion, currentAppRegion }}
              />
              <div>
                <Button
                  href={`^/logout?${queryString.build({
                    nextUrl: `${location.pathname}${location.search}`,
                  })}`}
                >
                  {t('common.logMeOut')}
                </Button>
              </div>
            </>
          )}
        </ContentBlock.Body>
      </ContentBlock>
    </>
  );
};
